import { type Item, type StandingLine, StandingOrderPut } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView, SearchBox } from '../components/ui';
import { formatQty, packLabel, weekdayName } from '../format';
import { useDebounced } from '../hooks';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

interface Draft {
  weekdays: number[];
  lines: { itemId: string; itemName: string; qty: string; unit: StandingLine['unit']; note: string }[];
  notes: string;
}

/** The pattern a customer orders on: which days, which items, how many. The app drafts it each delivery day. */
export function StandingPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const customer = useQuery({ queryKey: qk.customer(id), queryFn: ({ signal }) => api.customer(id, signal) });
  const standing = useQuery({ queryKey: qk.standing(id), queryFn: ({ signal }) => api.standingOrders(id, signal) });
  const [d, setD] = useState<Draft | null>(null);
  const [picking, setPicking] = useState(false);
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!standing.data || !customer.data || d) return;
    const s = standing.data[0];
    setD(
      s
        ? { weekdays: s.weekdays, notes: s.notes ?? '', lines: s.lines.map((l) => ({ itemId: l.itemId, itemName: l.itemName, qty: String(l.qty), unit: l.unit, note: l.note ?? '' })) }
        : { weekdays: customer.data?.deliveryDays ?? [], lines: [], notes: '' },
    );
  }, [standing.data, customer.data, d]);

  const save = useMutation({
    mutationFn: (body: StandingOrderPut) => api.putStandingOrder(id, body),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: qk.standing(id) });
    },
  });
  const retire = useMutation({
    mutationFn: () => api.deleteStandingOrder(id),
    onSuccess: () => {
      setD({ weekdays: customer.data?.deliveryDays ?? [], lines: [], notes: '' });
      void qc.invalidateQueries({ queryKey: qk.standing(id) });
    },
  });

  function submit() {
    if (!d) return;
    if (d.weekdays.length === 0 || d.lines.length === 0) return setProblem('nothingToSave');
    const lines: StandingLine[] = [];
    for (const l of d.lines) {
      const qty = parseNumber(l.qty);
      if (qty === null || qty <= 0) return setProblem('error');
      lines.push({ itemId: l.itemId, qty, unit: l.unit, ...(l.note.trim() ? { note: l.note.trim() } : {}) });
    }
    const parsed = StandingOrderPut.safeParse({ weekdays: d.weekdays, lines, notes: d.notes.trim() || null, active: true });
    if (!parsed.success) return setProblem('error');
    setProblem(null);
    setSaved(false);
    save.mutate(parsed.data);
  }

  return (
    <QueryView query={customer}>
      {(c) => (
        <div className="stack">
          <PageHead title={`${t('standingOrder')}: ${c.name}`} back={`/customers/${c.id}`} />
          <p className="muted">{t('standingHint')}</p>
          {saved ? (
            <div className="flash" role="status">
              ✓ {t('saved')}
            </div>
          ) : null}
          {d ? (
            <>
              <section className="card stack-sm">
                <h2>{t('deliveryDays')}</h2>
                <div className="chips" role="group" aria-label={t('deliveryDays')}>
                  {[1, 2, 3, 4, 5, 6, 7].map((w) => (
                    <button
                      key={w}
                      type="button"
                      className="chip"
                      aria-pressed={d.weekdays.includes(w)}
                      onClick={() => {
                        setProblem(null);
                        setD({ ...d, weekdays: d.weekdays.includes(w) ? d.weekdays.filter((x) => x !== w) : [...d.weekdays, w].sort((a, b) => a - b) });
                      }}
                    >
                      {weekdayName(w, intlLocale)}
                    </button>
                  ))}
                </div>
              </section>

              <section className="card stack-sm">
                <h2>{t('items')}</h2>
                {d.lines.length === 0 ? (
                  <Empty>{t('addItems')}</Empty>
                ) : (
                  <ul className="list">
                    {d.lines.map((l, i) => (
                      <li key={`${l.itemId}:${i}`} className="row">
                        <input
                          className="count-input"
                          style={{ width: 72, minWidth: 72 }}
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          aria-label={`${t('qty')}: ${l.itemName}`}
                          value={l.qty}
                          onChange={(e) => setD({ ...d, lines: d.lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)) })}
                        />
                        <span className="row-main">
                          <span className="row-title">{l.itemName}</span>
                          <span className="row-sub">{l.unit}</span>
                        </span>
                        <button type="button" className="icon-btn" aria-label={`${t('remove')}: ${l.itemName}`} onClick={() => setD({ ...d, lines: d.lines.filter((_, j) => j !== i) })}>
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {picking ? (
                  <ItemPicker
                    onPick={(item) => {
                      setD({ ...d, lines: [...d.lines, { itemId: item.id, itemName: item.name, qty: '1', unit: item.packUnit, note: '' }] });
                      setPicking(false);
                    }}
                    onCancel={() => setPicking(false)}
                  />
                ) : (
                  <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => setPicking(true)}>
                    + {t('addItem')}
                  </button>
                )}
              </section>

              <label className="field">
                <span>{t('notes')}</span>
                <textarea value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} />
              </label>
              {problem ? (
                <p className="field-error" role="alert">
                  {t(problem)}
                </p>
              ) : null}
              {save.error ? <ErrorBox error={save.error} /> : null}
              <button type="button" className="btn btn-primary btn-lg btn-block" onClick={submit} disabled={save.isPending}>
                {save.isPending ? t('sending') : t('save')}
              </button>
              {standing.data?.[0] ? (
                <button type="button" className="btn btn-ghost btn-block" onClick={() => { if (window.confirm(`${t('retireStanding')}: ${t('confirmSure')}`)) retire.mutate(); }} disabled={retire.isPending}>
                  {t('retireStanding')}
                </button>
              ) : null}
              {standing.data?.[0] ? (
                <p className="note">
                  {t('standingSummary')}: {standing.data[0].weekdays.map((w) => weekdayName(w, intlLocale)).join(', ')} · {standing.data[0].lines.length} {t('lines')} ·{' '}
                  {formatQty(standing.data[0].lines.reduce((s, l) => s + l.qty, 0))} {t('packs')}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </QueryView>
  );
}

function ItemPicker({ onPick, onCancel }: { onPick: (item: Item) => void; onCancel: () => void }) {
  const { t } = useLocale();
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim());
  const list = useQuery({ queryKey: qk.items(term), queryFn: ({ signal }) => api.items(term, signal), placeholderData: keepPreviousData });
  return (
    <div className="picker">
      <div className="picker-head">
        <SearchBox value={q} onChange={setQ} autoFocus />
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('cancel')}
        </button>
      </div>
      <QueryView query={list}>
        {(rows) => {
          const shown = rows.filter((i) => i.active).slice(0, 30);
          return shown.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {shown.map((item) => (
                <li key={item.id}>
                  <button type="button" className="row" onClick={() => onPick(item)}>
                    <span className="row-main">
                      <span className="row-title">{item.name}</span>
                      <span className="row-sub">{packLabel(item)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}
